/**
 * The one place a navigation entry is tied to a glyph.
 *
 * Tabler ships close to six thousand icons, so picking one is a judgement call
 * rather than a lookup. Keeping the whole set here means changing a symbol is a
 * one-line edit against a name, with nothing to hunt for in the shell.
 *
 * Every name is of the form `TbOutline<Thing>` or `TbFill<Thing>`. Only about a
 * sixth of the set has a filled variant, so these are all outline: a mixed rail
 * reads as a mistake rather than a choice.
 */
import {
  TbOutlineAddressBook,
  TbOutlineBuilding,
  TbOutlineChartRadar,
  TbOutlineClipboardList,
  TbOutlineDashboard,
  TbOutlineFilter,
  TbOutlineLogout,
  TbOutlinePackage,
  TbOutlineReportAnalytics,
  TbOutlineTable,
  TbOutlineSitemap,
  TbOutlineUsers,
} from "solid-icons/tb";
import type { IconTypes } from "solid-icons";

/** Everything the rail can point at, including the action at its foot. */
export type NavKey =
  | "dashboard"
  | "catalog"
  | "products"
  | "personas"
  | "questionnaires"
  | "authors"
  | "customers"
  | "views"
  | "overview"
  | "compare"
  | "reports"
  | "logout";

/**
 * Swap any of these for another Tabler name to change the rail. The reasoning
 * behind each is written down so a later change can disagree with it knowingly.
 */
export const navIcons: Record<NavKey, IconTypes> = {
  /** The overview screen, which is what the glyph is named after. */
  dashboard: TbOutlineDashboard,
  /** A tree of topics, capabilities and questions, so a hierarchy rather than a book. */
  catalog: TbOutlineSitemap,
  /** One CMS offering. A parcel says "a thing being compared" without implying software. */
  products: TbOutlinePackage,
  /** Several people whose needs are held side by side, never blended. */
  personas: TbOutlineUsers,
  /** A form sent out to be filled in. */
  questionnaires: TbOutlineClipboardList,
  /** The people a questionnaire is addressed to, so a contact list. */
  authors: TbOutlineAddressBook,
  customers: TbOutlineBuilding,
  /** A stored filter. The example in the brief was the filled twin of this. */
  views: TbOutlineFilter,
  /** Every answer as a grid of questions against products, so a table. */
  overview: TbOutlineTable,
  /** Two products on one wheel, which is literally what the screen draws. */
  compare: TbOutlineChartRadar,
  /** The evaluated output, which is a document carrying figures. */
  reports: TbOutlineReportAnalytics,
  /** Leaving, not a destination, which is why it sits apart from the links. */
  logout: TbOutlineLogout,
};
